import {
  CoverageStatus,
  CoverageStatusDisplayName,
} from "pmt-types/src/enums/CoverageStatus.ts";
import {
  InsuranceType,
  InsuranceTypeDisplayName,
} from "pmt-types/src/enums/InsuranceType.ts";
import {
  ExcessLiabilityPolicy,
  ExcessLiabilityPolicySchema,
} from "pmt-types/src/models/ExcessLiabilityPolicy.ts";

import { Source } from "pmt-types/src/models/Source.ts";
import FormInput from "../../../components/form/formInput.tsx";
import NumberInput from "../../../components/form/numberInput.tsx";
import PmtForm from "../../../components/form/pmtForm.tsx";
import SearchableSelect from "../../../components/form/searchableSelect.tsx";
import FormRoot from "../../../components/form/style/formRoot.tsx";
import FormSection from "../../../components/form/style/formSection.tsx";
import TabbedCheckbox from "../../../components/form/tabbedCheckbox.tsx";
import TabbedSelect from "../../../components/form/tabbedSelect.tsx";
import TabularInputs from "../../../components/form/tabularInputs.tsx";
import { useMutationSearchInsurers } from "../../../hooks/mutations/useMutationSearchInsurers.ts";
import enumToLabelValue from "../../../utils/transforms/enumToLabelValue.ts";
import { transformSelectInput } from "../../../utils/transforms/transformSelectInput.ts";
import { Maybe } from "../../../utils/types/maybe.ts";

type Props = {
  source: Maybe<Source>;
  policyIndex: number;
  onSubmit: (policy: ExcessLiabilityPolicy) => void;
  onCancel: () => void;
  onDelete: () => void;
};

type LimitTableDefinition = {
  type: string;
  limit: JSX.Element;
};

type OptionTableDefinition = {
  name: string;
  applicable: JSX.Element;
};

export default function ExcessLiabilityPolicyForm({
  source,
  policyIndex,
  onSubmit,
  onCancel,
  onDelete,
}: Props) {
  const { mutateAsync: searchInsurers } = useMutationSearchInsurers();

  const defaultInsurer =
    source?.excessLiabilities != null && source?.excessLiabilities?.length > 0
      ? source.excessLiabilities[source.excessLiabilities.length - 1].insurer
      : source?.generalLiability?.insurer ?? undefined;

  const defaultFormValues = {
    insurer: defaultInsurer,
    typeOfInsurance: InsuranceType.enum.Occurrence,
    limits: {
      eachOccurrence: 2000000,
      generalAggregate: 4000000,
      products: 4000000,
    },
  };

  const existingPolicy =
    policyIndex == -1 ? null : source?.excessLiabilities?.at(policyIndex);

  const limits = [
    {
      label: "Each Occurrence",
      limitName: "limits.eachOccurrence",
    },
    {
      label: "General Aggregate",
      limitName: "limits.generalAggregate",
    },
    {
      label: "Products - Comp/OP Aggregate",
      limitName: "limits.products",
    },
  ];

  const options = [
    {
      label: "Umbrella Liability",
      optionName: "options.umbrellaLiability",
    },
    {
      label: "Excess Liability",
      optionName: "options.excessLiability",
    },
    {
      label: "Retention Liability",
      optionName: "options.retentionLiability",
    },
    {
      label: "Deductible Liability",
      optionName: "options.deductibleLiability",
    },
  ];

  return (
    <PmtForm<ExcessLiabilityPolicy>
      schema={ExcessLiabilityPolicySchema}
      defaultValues={{
        ...defaultFormValues,
        ...existingPolicy,
      }}
      onSave={onSubmit}
      onCancel={onCancel}
      isSubmitting={false}
      formStyle="inline"
      renderForm={() => (
        <FormRoot>
          <FormSection>
            <FormInput name="policyNumber" label="Policy Number" />
            <FormInput
              name="effectiveDate"
              label="Effective Date"
              type="date"
            />
            <FormInput
              name="expirationDate"
              label="Expiration Date"
              type="date"
            />
            <SearchableSelect
              name="insurer"
              label="Insurer"
              transform={transformSelectInput}
              queryFn={searchInsurers}
            />
            <TabbedSelect
              name="status"
              label="Status"
              tabs={enumToLabelValue(
                CoverageStatus.enum,
                CoverageStatusDisplayName,
              )}
            />
            <TabularInputs
              label="Limits"
              tableDef={[
                {
                  header: "Type",
                  component: (limitDef: LimitTableDefinition) => limitDef.type,
                },
                {
                  header: "Limit",
                  component: (limitDef: LimitTableDefinition) => limitDef.limit,
                },
              ]}
              rows={limits.map((limit) => ({
                type: limit.label,
                limit: (
                  <NumberInput
                    name={limit.limitName}
                    prefix="$"
                    min={0}
                    step={50000}
                  />
                ),
              }))}
            />
            <TabbedSelect
              name="typeOfInsurance"
              label="Type of Insurance"
              tabs={enumToLabelValue(
                InsuranceType.enum,
                InsuranceTypeDisplayName,
              )}
            />
            <TabularInputs
              label="Options"
              tableDef={[
                {
                  header: "Name",
                  component: (optionDef: OptionTableDefinition) =>
                    optionDef.name,
                },
                {
                  header: "Applicable",
                  component: (optionDef: OptionTableDefinition) =>
                    optionDef.applicable,
                },
              ]}
              rows={options.map((option) => ({
                name: option.label,
                applicable: <TabbedCheckbox name={option.optionName} />,
              }))}
            />
            {existingPolicy != null ? (
              <button
                type="button"
                className="mt-4 w-[180px] text-center relative inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                onClick={onDelete}
              >
                Delete this coverage?
              </button>
            ) : (
              <></>
            )}
          </FormSection>
        </FormRoot>
      )}
    />
  );
}
